/**
 * ScenarioSelectionStep — Create Test Wizard
 * Step 6F Green Phase
 */

import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { SCENARIO_LIST, SCENARIOS, type ScenarioId } from "../../constants/scenarios";
import type { WizardStepProps } from "./VersionSelectionStep";

export function ScenarioSelectionStep({ wizardData, onNext, onBack }: WizardStepProps) {
  const [selectedScenario, setSelectedScenario] = useState<string>((wizardData?.scenario as string) ?? "");

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Select Assessment Scenario</h2>
      <p className="text-sm text-brand-steel mb-6">
        Choose the type of assessment you want to create. This determines the flow, inputs, and comparison logic.
      </p>

      <Card className="border-brand-steel/50 shadow-sm mb-6">
        <CardContent className="py-6 px-8">
          <RadioGroup
            value={selectedScenario}
            onValueChange={setSelectedScenario}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {SCENARIO_LIST.map((scenario) => {
              const isSelected = selectedScenario === scenario.id;
              return (
                <label
                  key={scenario.id}
                  className={`flex items-start gap-3 p-4 bg-white border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? "border-brand-crimson bg-brand-crimson/5"
                      : "border-brand-steel/40 hover:bg-brand-panel/50"
                  }`}
                >
                  <RadioGroupItem
                    value={scenario.id}
                    className="mt-0.5 shrink-0 border-brand-steel/70 data-[state=checked]:border-brand-crimson data-[state=checked]:bg-brand-crimson"
                  />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">{scenario.name}</h4>
                    <p className="text-xs text-brand-steel">{scenario.description}</p>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <div>
          {onBack && (
            <Button type="button" variant="outline" onClick={() => onBack?.()} className="min-w-25 border-brand-steel/40">
              Back
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={() => {
            const selected = SCENARIOS[selectedScenario as ScenarioId];
            onNext?.({
              scenario: selectedScenario,
              scenario_name: selected?.name ?? "",
              scenario_description: selected?.description ?? "",
            });
          }}
          disabled={!selectedScenario}
          className="min-w-25 bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-6 py-2"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default ScenarioSelectionStep;
